import React, { useEffect, useRef, useState } from 'react';
import mapboxgl, { Map, Marker } from 'mapbox-gl';
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';
import './LocationPicker.scss';

interface ILocationPickerProps {
  onLocationSelect: (location: {
    address: string;
    latitude: number;
    longitude: number;
  }) => void;
  initialLocation?: {
    address?: string;
    latitude?: number;
    longitude?: number;
  };
  placeholder?: string;
}

const LocationPicker: React.FC<ILocationPickerProps> = ({
  onLocationSelect,
  initialLocation,
  placeholder = 'Rechercher une adresse...',
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const mapRef = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<Map | null>(null);
  const [marker, setMarker] = useState<Marker | null>(null);
  const [selectedAddress, setSelectedAddress] = useState(initialLocation?.address || '');

  const reverseGeocode = async (lng: number, lat: number) => {
    try {
      const token = process.env.RAZZLE_MAPBOX_ACCESS_TOKEN || process.env.MAPBOX_ACCESS_TOKEN;
      if (!token) return;
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json?access_token=${token}`,
      );
      const data = (await response.json()) as { features?: { place_name?: string }[] };
      const address = data.features?.[0]?.place_name || '';

      setSelectedAddress(address);
      if (inputRef.current) inputRef.current.value = address;

      onLocationSelect({
        address,
        latitude: lat,
        longitude: lng,
      });
    } catch (_e) {
      // ignore
    }
  };

  // Initialiser Mapbox
  useEffect(() => {
    if (!mapRef.current) return;
    const token = process.env.RAZZLE_MAPBOX_ACCESS_TOKEN || process.env.MAPBOX_ACCESS_TOKEN;
    if (!token) return;

    mapboxgl.accessToken = token;

    const initialCenter: [number, number] =
      initialLocation?.latitude && initialLocation?.longitude
        ? [initialLocation.longitude, initialLocation.latitude]
        : [2.3522, 48.8566];

    const mapInstance = new mapboxgl.Map({
      container: mapRef.current,
      style: 'mapbox://styles/mapbox/streets-v12',
      center: initialCenter,
      zoom: 15,
    });

    mapInstance.addControl(new mapboxgl.NavigationControl(), 'top-right');

    const markerInstance = new mapboxgl.Marker({ draggable: true }).setLngLat(initialCenter).addTo(mapInstance);
    markerInstance.on('dragend', () => {
      const lngLat = markerInstance.getLngLat();
      reverseGeocode(lngLat.lng, lngLat.lat);
    });

    mapInstance.on('click', (e) => {
      markerInstance.setLngLat([e.lngLat.lng, e.lngLat.lat]);
      reverseGeocode(e.lngLat.lng, e.lngLat.lat);
    });

    setMap(mapInstance);
    setMarker(markerInstance);

    return () => {
      mapInstance.remove();
    };
  }, []);

  // Initialiser le geocoder Mapbox
  useEffect(() => {
    if (!map || !inputRef.current) return;
    const token = process.env.RAZZLE_MAPBOX_ACCESS_TOKEN || process.env.MAPBOX_ACCESS_TOKEN;
    if (!token) return;

    const geocoder = new MapboxGeocoder({
      accessToken: token,
      mapboxgl,
      placeholder,
      marker: false,
    });

    const parent = inputRef.current.parentElement;
    if (!parent) return;

    inputRef.current.style.display = 'none';
    parent.appendChild(geocoder.onAdd(map) as unknown as Node);

    geocoder.on('result', (event: { result?: { place_name?: string; center?: [number, number] } }) => {
      const center = event?.result?.center;
      const address = event?.result?.place_name || '';
      if (!center || !marker) return;

      marker.setLngLat(center);
      map.flyTo({ center, zoom: 15 });

      setSelectedAddress(address);
      onLocationSelect({
        address,
        latitude: center[1],
        longitude: center[0],
      });
    });

    return () => {
      try {
        geocoder.clear();
      } catch (_e) {
        // ignore
      }
    };
  }, [map, marker, placeholder]);

  return (
    <div className="location-picker">
      <div className="location-picker-search">
        <input
          ref={inputRef}
          type="text"
          className="form-control"
          placeholder={placeholder}
          defaultValue={selectedAddress}
        />
        <small className="form-text text-muted mt-1">
          <i className="fas fa-info-circle"></i> Recherchez une adresse ou cliquez sur la carte
        </small>
      </div>
      
      <div 
        ref={mapRef}
        className="location-picker-map"
        style={{ height: '300px', width: '100%', marginTop: '1rem', borderRadius: '8px' }}
      />
    </div>
  );
};

export default LocationPicker;
